import { PrismaEnums } from '@airreading/schema/browser';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { userLikeSchema } from './user.js';

export const substitutionRequestTypeSchema = z.enum(['requested_sub_by_coach', 'coach_no_show', 'assigned_by_admin']);
export type SubstitutionRequestType = z.infer<typeof substitutionRequestTypeSchema>;
export const SubstitutionRequestType = substitutionRequestTypeSchema.enum;

export const SubstitutionRequestTypeLabelMap: Record<SubstitutionRequestType, string> = {
  [SubstitutionRequestType.requested_sub_by_coach]: 'Requested by coach',
  [SubstitutionRequestType.coach_no_show]: 'Coach no show',
  [SubstitutionRequestType.assigned_by_admin]: 'Assigned by admin',
};

export const substitutionRequestStatusSchema = z.nativeEnum(PrismaEnums.SubstitutionRequestStatus);
export type SubstitutionRequestStatus = z.infer<typeof substitutionRequestStatusSchema>;
export const SubstitutionRequestStatus = substitutionRequestStatusSchema.enum;

export const substitutionRequestOriginSchema = z.enum(['manual', 'automatic_conflict']);
export type SubstitutionRequestOrigin = z.infer<typeof substitutionRequestOriginSchema>;
export const SubstitutionRequestOrigin = substitutionRequestOriginSchema.enum;

export const substitutionRequestSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  requestedById: z.string(),
  requestedByCoach: z.lazy(() => userLikeSchema),
  substituteId: z.string().nullable(),
  substituteCoach: z.lazy(() => userLikeSchema).nullable(),
  requestReason: z.string().nullable(),
  status: substitutionRequestStatusSchema,
  type: substitutionRequestTypeSchema,
  createdAtInstant: z.instanceof(Temporal.Instant),
  updatedAtInstant: z.instanceof(Temporal.Instant),
  hoursUntilSessionStart: z.number(),
  acceptedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  canceledAtInstant: z.instanceof(Temporal.Instant).nullable(),
  origin: substitutionRequestOriginSchema,
});

export type SubstitutionRequest = z.infer<typeof substitutionRequestSchema>;

/** Scalar fields only — sufficient for {@link getSessionSubstitutionRequestSummary}. */
export const coachPortalSubstitutionRequestSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  requestedById: z.string(),
  substituteId: z.string().nullable(),
  status: substitutionRequestStatusSchema,
  type: substitutionRequestTypeSchema,
  createdAtInstant: z.instanceof(Temporal.Instant),
  hoursUntilSessionStart: z.number(),
  acceptedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  origin: substitutionRequestOriginSchema,
});

export type CoachPortalSubstitutionRequest = z.infer<typeof coachPortalSubstitutionRequestSchema>;

export const createSubstitutionRequestByCoachSchema = z.object({
  sessionId: z.string(),
  requestReason: z.string().nullable().optional(),
});
export type CreateSubstitutionByCoachRequest = z.infer<typeof createSubstitutionRequestByCoachSchema>;

export type CoachSubstitutionInvolvement =
  | 'requested_req_pending'
  | 'requested_req_fulfilled'
  | 'fulfilled_request'
  | 'none';
